// src/components/backend/companies/CompaniesTable.jsx
import React from "react";
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableSortLabel,
    Switch,
    IconButton,
    Tooltip,
    Typography,
    Box,
} from "@mui/material";
import {
    Edit as EditIconDefault,
    Delete as DeleteIconDefault,
    Visibility as VisibilityIconDefault,
    BarChart as BarChartIconDefault,
    History as HistoryIconDefault,
    Print as PrintIconDefault,
} from "@mui/icons-material";

function formatDateTime(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Berlin",
    });
}

function formatLastChange(change) {
    if (!change) return "-";
    const user = change.username || "Unbekannt";
    const date = formatDateTime(change.changed_at);
    return { user, date };
}

export default function CompaniesTable({
                                           rows = [],
                                           columns = [],
                                           order = "asc",
                                           orderBy = "name",
                                           onRequestSort,
                                           onEdit,
                                           onDelete,
                                           onToggleActive,
                                           onOpenLive,
                                           onPrintLive,
                                           onOpenStats,
                                           onOpenHistory,
                                           icons,
                                       }) {
    // Comments in English: Use passed icons if provided, otherwise fallback to MUI defaults.
    const EditIcon = icons?.EditIcon || EditIconDefault;
    const DeleteIcon = icons?.DeleteIcon || DeleteIconDefault;
    const VisibilityIcon = icons?.VisibilityIcon || VisibilityIconDefault;
    const BarChartIcon = icons?.BarChartIcon || BarChartIconDefault;
    const HistoryIcon = icons?.HistoryIcon || HistoryIconDefault;
    const PrintIcon = icons?.PrintIcon || PrintIconDefault;

    const renderHeaderLabel = (column) => {
        if (column.id === "page_views") {
            return (
                <Tooltip title="Seitenansichten">
                    <BarChartIcon fontSize="small" />
                </Tooltip>
            );
        }
        if (column.id === "last_change") {
            return (
                <Tooltip title="Letzte Änderung">
                    <HistoryIcon fontSize="small" />
                </Tooltip>
            );
        }
        return column.label;
    };

    return (
        <Table size="small" sx={{ "& th, & td": { py: 0.6, px: 1.25 }, "& .MuiIconButton-root": { p: 0.45 } }}>
            <TableHead>
                <TableRow>
                    {columns.map((c) => (
                        <TableCell
                            key={c.id}
                            align={c.id === "page_views" ? "center" : "left"}
                            sx={{
                                width: c.id === "page_views" ? 54 : c.id === "last_change" ? 132 : undefined,
                                whiteSpace: "nowrap",
                            }}
                        >
                            <TableSortLabel
                                active={orderBy === c.id}
                                direction={orderBy === c.id ? order : "asc"}
                                onClick={() => onRequestSort?.(c.id)}
                            >
                                {renderHeaderLabel(c)}
                            </TableSortLabel>
                        </TableCell>
                    ))}
                    <TableCell align="right" sx={{ width: 172, whiteSpace: "nowrap" }}>Aktionen</TableCell>
                </TableRow>
            </TableHead>

            <TableBody>
                {rows.map((c) => (
                    <TableRow
                        key={c.id}
                        hover
                        sx={{ opacity: c.is_active ? 1 : 0.55 }}
                    >
                        {/* Aktiv (Spalte is_active) */}
                        <TableCell>
                            <Switch
                                size="small"
                                checked={!!c.is_active}
                                onChange={() => onToggleActive?.(c)}
                                sx={{ transform: "scale(0.82)" }}
                            />
                        </TableCell>

                        {/* Restliche Spalten (ohne is_active) */}
                        {columns
                            .filter((col) => col.id !== "is_active")
                            .map((col) => {
                                let value = "-";

                                switch (col.id) {
                                    case "customer_number":
                                        value = c.customer_number || "-";
                                        break;
                                    case "name":
                                        value = c.name || "-";
                                        break;
                                    case "street":
                                        value = c.street || "-";
                                        break;
                                    case "zip":
                                        value = c.zip || "-";
                                        break;
                                    case "city":
                                        value = c.city || "-";
                                        break;
                                    case "country":
                                        value = c.country || "-";
                                        break;
                                    case "lat":
                                        value = c.latitude ?? "-";
                                        break;
                                    case "lon":
                                        value = c.longitude ?? "-";
                                        break;
                                    case "page_views":
                                        value = c.page_views ?? 0;
                                        break;
                                    case "last_change":
                                        const lastChange = formatLastChange(c.last_change);
                                        value = (
                                            lastChange === "-" ? "-" : (
                                                <Box sx={{ lineHeight: 1.15, minWidth: 0 }}>
                                                    <Typography variant="caption" sx={{ display: "block", fontWeight: 600, whiteSpace: "nowrap" }}>
                                                        {lastChange.user}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", whiteSpace: "nowrap" }}>
                                                        {lastChange.date || "-"}
                                                    </Typography>
                                                </Box>
                                            )
                                        );
                                        break;
                                    default:
                                        value = c?.[col.id] ?? "-";
                                        break;
                                }

                                return (
                                    <TableCell
                                        key={col.id}
                                        align={col.id === "page_views" ? "center" : "left"}
                                        sx={{
                                            width: col.id === "page_views" ? 54 : col.id === "last_change" ? 132 : undefined,
                                            maxWidth: col.id === "last_change" ? 150 : undefined,
                                        }}
                                    >
                                        {value}
                                    </TableCell>
                                );
                            })}

                        {/* Aktionen */}
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            <Tooltip title="Statistiken">
                                <IconButton onClick={() => onOpenStats?.(c)} aria-label="Statistiken">
                                    <BarChartIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Änderungshistorie">
                                <IconButton onClick={() => onOpenHistory?.(c)} aria-label="Änderungshistorie">
                                    <HistoryIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Live Ansicht">
                                <IconButton onClick={() => onOpenLive?.(c)} aria-label="Live Ansicht">
                                    <VisibilityIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Live Ansicht drucken">
                                <IconButton onClick={() => onPrintLive?.(c)} aria-label="Live Ansicht drucken">
                                    <PrintIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Bearbeiten">
                                <IconButton onClick={() => onEdit?.(c)} aria-label="Bearbeiten">
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Löschen">
                                <IconButton color="error" onClick={() => onDelete?.(c)} aria-label="Löschen">
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}
